import { useCallback, useEffect, useRef } from "react";

const scrollRoot = () => document.scrollingElement || document.documentElement;

// Include every ancestor so an inner panel at its limit can hand off to the page.
export const dragScrollOffset = (element) => {
  let x = 0; let y = 0;
  for (let node = element; node; node = node.parentElement) {
    x += node.scrollLeft || 0; y += node.scrollTop || 0;
  }
  return { x, y };
};

export const createDragAutoScroller = ({ onScroll, isActive = () => true, axes = "both" } = {}) => {
  let frame = null; let pointer = null; let previousTime = null;
  const stop = () => {
    if (frame !== null) window.cancelAnimationFrame(frame);
    frame = null; pointer = null; previousTime = null;
  };
  const step = (time) => {
    frame = null;
    if (!pointer || !isActive() || !pointer.target?.isConnected) { stop(); return; }
    const elapsed = previousTime === null ? 16 : Math.min(32, Math.max(0, time - previousTime));
    previousTime = time;
    const root = scrollRoot();
    let moved = false;
    for (const axis of axes === "both" ? ["y", "x"] : [axes]) {
      const vertical = axis === "y";
      const position = vertical ? pointer.y : pointer.x;
      const viewportSize = vertical ? window.innerHeight : window.innerWidth;
      const scrollKey = vertical ? "scrollTop" : "scrollLeft";
      const sizeKey = vertical ? "scrollHeight" : "scrollWidth";
      const clientKey = vertical ? "clientHeight" : "clientWidth";
      const lowKey = vertical ? "top" : "left";
      const highKey = vertical ? "bottom" : "right";
      for (let node = pointer.target; node; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (node === root) {
          const bodyStyle = window.getComputedStyle(document.body);
          const overflowKey = vertical ? "overflowY" : "overflowX";
          if (/hidden|clip/.test(style[overflowKey] || style.overflow)
            || /hidden|clip/.test(bodyStyle[overflowKey] || bodyStyle.overflow)) continue;
        }
        if (node !== root && !/auto|scroll|overlay/.test(style[vertical ? "overflowY" : "overflowX"] || style.overflow)) continue;
        const limit = node[sizeKey] - node[clientKey];
        if (limit <= 0) continue;
        const bounds = node === root ? { [lowKey]: 0, [highKey]: viewportSize } : node.getBoundingClientRect();
        let low = Math.max(0, bounds[lowKey]); let high = Math.min(viewportSize, bounds[highKey]);
        // A scroll panel may itself be clipped by another panel or modal.
        for (let parent = node.parentElement; parent && parent !== root; parent = parent.parentElement) {
          const parentStyle = window.getComputedStyle(parent);
          if (!/auto|scroll|hidden|clip/.test(parentStyle[vertical ? "overflowY" : "overflowX"] || parentStyle.overflow)) continue;
          const clip = parent.getBoundingClientRect();
          low = Math.max(low, clip[lowKey]); high = Math.min(high, clip[highKey]);
        }
        const edge = Math.min(90, (high - low) / 4);
        if (edge <= 0) continue;
        let speed = 0;
        if (position < low + edge) speed = -Math.min(1, (low + edge - position) / edge);
        else if (position > high - edge) speed = Math.min(1, (position - high + edge) / edge);
        if (!speed) continue;
        const previous = node[scrollKey];
        node[scrollKey] = Math.max(0, Math.min(limit, previous + speed * .8 * elapsed));
        if (node[scrollKey] !== previous) { moved = true; break; }
      }
    }
    if (moved) {
      onScroll?.();
      if (pointer && frame === null) frame = window.requestAnimationFrame(step);
    } else previousTime = null;
  };
  return {
    update: ({ clientX, clientY, target }) => {
      pointer = { x: clientX, y: clientY, target: target?.nodeType === 1 ? target : target?.parentElement };
      if (frame === null) frame = window.requestAnimationFrame(step);
    },
    stop,
  };
};

// One installation covers native ordering, moving between lists, and OS file uploads.
export const installNativeDragAutoScroll = () => {
  let source = null; let external = false; let generation = 0;
  const scroller = createDragAutoScroller({ isActive: () => external || Boolean(source?.isConnected && source.draggable) });
  const finish = () => { generation += 1; scroller.stop(); source = null; external = false; };
  const start = (event) => {
    finish();
    const currentGeneration = generation;
    const element = event.target.closest?.('[draggable="true"]');
    // React may reject a drag at its bubbling handler; inspect after dispatch.
    queueMicrotask(() => { if (currentGeneration === generation && !event.defaultPrevented && element?.isConnected) source = element; });
  };
  const track = (event) => {
    const files = Array.from(event.dataTransfer?.types || []).includes("Files");
    if (!source && !files) return;
    external = !source && files;
    let target = document.elementFromPoint?.(event.clientX, event.clientY) || event.target || source;
    // In the gap just outside a list, continue scrolling its source panel.
    let insidePanel = false;
    for (let node = target?.nodeType === 1 ? target : null; node && node !== scrollRoot(); node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if ((/auto|scroll|overlay/.test(style.overflowY || style.overflow) && node.scrollHeight > node.clientHeight)
        || (/auto|scroll|overlay/.test(style.overflowX || style.overflow) && node.scrollWidth > node.clientWidth)) { insidePanel = true; break; }
    }
    if (!insidePanel && source) target = source;
    scroller.update({ clientX: event.clientX, clientY: event.clientY, target });
  };
  const leave = (event) => { if (!event.relatedTarget) scroller.stop(); };
  const key = (event) => { if (event.key === "Escape") finish(); };
  const listeners = [["dragstart", start], ["dragover", track], ["drop", finish], ["dragend", finish], ["dragleave", leave], ["pointerdown", finish], ["keydown", key]];
  listeners.forEach(([name, handler]) => document.addEventListener(name, handler, true));
  window.addEventListener("blur", finish);
  return () => {
    finish();
    listeners.forEach(([name, handler]) => document.removeEventListener(name, handler, true));
    window.removeEventListener("blur", finish);
  };
};

// Pointer gestures opt in only after their tool's guards accept a gesture.
// Replay movement after scrolling so stationary pointers still update crop/move geometry.
export const usePointerDragAutoScroll = () => {
  const cleanup = useRef(null);
  const stop = useCallback(() => { cleanup.current?.(); cleanup.current = null; }, []);
  useEffect(() => stop, [stop]);
  const start = useCallback((event, { axes = "both" } = {}) => {
    stop();
    const element = event.currentTarget;
    const pointerId = event.pointerId;
    let last = event.nativeEvent || event;
    let replaying = false;
    const scroller = createDragAutoScroller({ axes, onScroll: () => {
      const Pointer = window.PointerEvent || window.MouseEvent;
      replaying = true;
      try {
        element.dispatchEvent(new Pointer("pointermove", { bubbles: true, cancelable: true,
          clientX: last.clientX, clientY: last.clientY, pointerId,
          pointerType: last.pointerType, buttons: last.buttons, pressure: last.pressure }));
      } finally { replaying = false; }
    } });
    const move = (next) => {
      if (next.pointerId !== pointerId || replaying) return;
      last = next; scroller.update({ clientX: next.clientX, clientY: next.clientY, target: element });
    };
    const end = (next) => { if (next.pointerId === pointerId) stop(); };
    const key = (next) => { if (next.key === "Escape") stop(); };
    document.addEventListener("pointermove", move, true);
    document.addEventListener("pointerup", end, true);
    document.addEventListener("pointercancel", end, true);
    document.addEventListener("lostpointercapture", end, true);
    document.addEventListener("keydown", key, true);
    window.addEventListener("blur", stop);
    cleanup.current = () => {
      scroller.stop();
      document.removeEventListener("pointermove", move, true);
      document.removeEventListener("pointerup", end, true);
      document.removeEventListener("pointercancel", end, true);
      document.removeEventListener("lostpointercapture", end, true);
      document.removeEventListener("keydown", key, true);
      window.removeEventListener("blur", stop);
    };
  }, [stop]);
  return { start, stop };
};
